import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Circle, Fill, Stroke, Style } from 'ol/style';
import { LineString, MultiPoint } from 'ol/geom';
import { Feature } from 'ol';

import { InterpretationMode } from '../models/enums/InterpretationMode';
import { IGetCoordinatesDataResponse } from '../models/interfaces/IGetCoordinatesDataResponse';
import { IInterpretationDictionary } from '../hooks/useInterpretation2DSeismicViewer';
import { GeopostMap } from 'features/seismic/models/classes/GeopostMap';
import { ICoordinateString } from 'features/seismic/models/interfaces/ICoordinate';
import { calculateXPosition, CalculateXPositionWithIncrement, getAmplitude, getOriginalXByScale, getOriginalYByScale, getTrace, getXByTrace, getYByAmplitude } from 'features/seismic/utils/calculateScreenValuesUtils';

export const buildLayerStyle = (layer: VectorLayer<VectorSource>, data: IGetCoordinatesDataResponse, mode: InterpretationMode) => {
    const styles: Array<Style> = [];

    if (data) {
        styles.push(buildLayerStyleByData(data));
    }

    if (mode === InterpretationMode.edit) {
        styles.push(buildLayerModifyStyle());
    }

    layer.setStyle(styles);
};

export const buildCrossLayerStyle = (color: string) => {
    return new Style({
        image: new Circle({
            radius: 5,
            fill: new Fill({
                color: color + 'AA'
            }),
            stroke: new Stroke({
                color: '#fff',
                width: 2
            })
        })
    });
};

export const removeInteractions = (item?: IInterpretationDictionary, map?: GeopostMap) => {
    if (map && item) {
        if (item.modify) {
            map.removeInteraction(item.modify);
            item.modify = undefined;
        }

        if (item.draw) {
            map.removeInteraction(item.draw);
            item.draw = undefined;
        }

        if (item.dragbox) {
            map.removeInteraction(item.dragbox);
            item.dragbox = undefined;
        }
    }
};

export const buildLayer = (visible = true, valueToAddToZIndex = 0) => new VectorLayer({
    source: new VectorSource(),
    zIndex: 9999999999,
    visible: visible,
});

export const buildCoordinatesToSave = (features: Feature[], scaleX: number, scaleY: number, minTrace: number, isInverted: boolean, maxTrace: number, lineIncrement: number, totalTraces: number, sampleInterval: number) => {
    const coordinates: Array<Array<ICoordinateString>> = [];

    for (let feature of features) {
        const geometry = feature.getGeometry() as LineString | undefined;

        if (geometry) {
            const geomCoordinates = geometry.getCoordinates();
            const featureCoordinates: Array<ICoordinateString> = [];

            for (let i = 0; i < geomCoordinates.length; i++) {
                const coordinate = geomCoordinates[i];

                let trace = getTrace(coordinate[0], scaleX, minTrace, lineIncrement);
                if (isInverted) {
                    trace = maxTrace - trace + minTrace;
                }

                const x = CalculateXPositionWithIncrement(trace, minTrace, maxTrace, lineIncrement, minTrace, false, 1);

                const amplitude = parseInt(getAmplitude(coordinate[1], scaleY, sampleInterval));

                const y = getYByAmplitude(amplitude, 1, sampleInterval);

                if (isNaN(x) || isNaN(y)) {
                    console.error('Erro ao gerar coordenada');
                    continue;
                }

                featureCoordinates.push({
                    X: x.toString(),
                    Y: y.toString()
                });

            }

            if (featureCoordinates.length > 0){
                coordinates.push(featureCoordinates);
            }
        }
    }

    return coordinates;
};

const hexToRgb = (hex: string) => {
    // 1. Remove the hash if present
    hex = hex.replace(/^#/, '');

    // 2. Parse the hex string
    if (hex.length === 3) {
        // Split into array, map to double the characters (e.g. "f" => "ff"), and join
        hex = hex.split('').map(char => char + char).join('');
    }

    // 3. Check for valid length (must be 6 at this point)
    if (hex.length !== 6) {
        return null; // Invalid format
    }

    // 4. Convert to integers
    // "0x" tells parseInt strictly to look for hex, or use parseInt(chunk, 16)
    const bigint = parseInt(hex, 16);

    return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255
    };
};

const buildLayerStyleByData = (data: IGetCoordinatesDataResponse) => {
    const rgb = hexToRgb(data.Color);
    const rgbString = `rgb(${rgb?.r},${rgb?.g}, ${rgb?.b}, ${data.Opacity})`;
    return new Style({
        stroke: new Stroke({
            color: rgbString,
            width: data.Width,
        })
    });
};

const buildLayerModifyStyle = () => new Style({
    stroke: new Stroke({
        color: 'rgb(0,255,255)',
        width: 3
    }),
    fill: new Fill({
        color: 'rgba(0, 255, 255, 0.5)'
    }),
    image: new Circle({
        radius: 5,
        fill: new Fill({
            color: 'orange'
        })
    }),
    geometry: (feature) => {
        const geometry = feature.getGeometry();

        if (geometry) {
            const lineString = geometry as LineString;
            const coordinates = lineString.getCoordinates();
            return new MultiPoint(coordinates);
        }
    }
});